import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { Operator } from "../models/Operator";
import { useTableData } from "../hooks/useTableData";

interface OperatorsTableProps {
	operators: Operator[];
	selectedOperator: Operator | null;
	onRowSelected: (operator: Operator | null) => void;
	onDelete: (operator: Operator) => void;
}

export const OperatorsTable = ({
	operators,
	selectedOperator,
	onRowSelected,
	onDelete,
}: OperatorsTableProps) => {
	const gridRef = useRef<AgGridReact<Operator>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<Operator>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<Operator>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowId = useCallback((params: { data: Operator }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<Operator>
			ref={gridRef}
			rowData={operators}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedOperator?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
